import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Bar, Cell, ComposedChart, CartesianGrid, Line, XAxis, YAxis } from "recharts";

import { CaptainUsageTooltip, type CaptainUsageRow } from "./CaptainUsageTooltip";
import { BALANCE_CHART_COLOR } from "../../constants";

import { type ChartConfig, ChartContainer, ChartTooltip } from "@/components/ui/v2/chart";
import { useValueFormatter } from "@/utils/useValueFormatter";

const CURRENT_MONTH_OPACITY = 0.55;
const SERIES = ["fromCap", "fromTopUp"] as const;
const BAR_RADIUS = 4;

export function CaptainUsageChart({ rows }: { rows: CaptainUsageRow[] }) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();

  const config: ChartConfig = {
    fromCap: { label: t("captainBilling.usage.series.fromCap"), color: BALANCE_CHART_COLOR.cap },
    fromTopUp: { label: t("captainBilling.usage.series.fromTopUp"), color: BALANCE_CHART_COLOR.topUp },
    projected: { label: t("captainBilling.usage.series.projected"), color: "var(--muted-foreground)" },
    cap: { label: t("captainBilling.usage.series.cap"), color: "var(--muted-foreground)" },
  };

  return (
    <div className="space-y-2">
      <ChartContainer config={config} className="aspect-auto h-[280px] w-full" aria-hidden>
        <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(label: string, index) => (rows[index]?.isCurrent ? `${label}*` : label)}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            allowDecimals={false}
            tickFormatter={(value) => format(Number(value), "whole")}
          />
          <ChartTooltip cursor={{ fill: "var(--muted)", opacity: 0.4 }} content={<CaptainUsageTooltip />} />
          {SERIES.map((series, index) => (
            <Bar
              key={series}
              dataKey={series}
              stackId="usage"
              fill={`var(--color-${series})`}
              radius={index === SERIES.length - 1 ? [BAR_RADIUS, BAR_RADIUS, 0, 0] : 0}
              isAnimationActive={false}
            >
              {rows.map((row) => (
                <Cell key={row.month} fillOpacity={row.isCurrent ? CURRENT_MONTH_OPACITY : 1} />
              ))}
            </Bar>
          ))}
          <Bar
            dataKey="projected"
            stackId="usage"
            fill="transparent"
            stroke="var(--color-projected)"
            strokeDasharray="4 3"
            strokeWidth={1.25}
            radius={[BAR_RADIUS, BAR_RADIUS, 0, 0]}
            isAnimationActive={false}
          />
          <Line
            dataKey="cap"
            type="stepAfter"
            stroke="var(--color-cap)"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ChartContainer>
      <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-muted-foreground" data-testid="captain-usage-legend">
        {SERIES.map((series) => (
          <LegendItem key={series} label={config[series].label}>
            <span className="size-2.5 rounded-[2px]" style={{ backgroundColor: config[series].color }} />
          </LegendItem>
        ))}
        <LegendItem label={config.projected.label}>
          <span className="size-2.5 rounded-[2px] border border-dashed border-muted-foreground" />
        </LegendItem>
        <LegendItem label={config.cap.label}>
          <span className="w-3.5 border-t-[1.5px] border-dashed border-muted-foreground" />
        </LegendItem>
        {rows.some((row) => row.isCurrent) && (
          <LegendItem label={t("captainBilling.usage.currentMonth")}>
            <span className="w-2.5 text-center font-semibold leading-none text-foreground">*</span>
          </LegendItem>
        )}
      </ul>
    </div>
  );
}

function LegendItem({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <li className="flex items-center gap-1.5 whitespace-nowrap">
      <span aria-hidden className="flex shrink-0 items-center">{children}</span>
      {label}
    </li>
  );
}
